import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, GraduationCap, Users, FileText, X, Loader } from 'lucide-react';
import { fetchDosen, fetchTendik, getImageUrl } from '../utils/api';

const STATIC_PAGES = [
  { label: 'Beranda', path: '/', desc: 'Halaman utama STIKES Harapan Ibu Jambi', keywords: 'beranda home utama' },
  { label: 'Sejarah Kampus', path: '/sejarah', desc: 'Sejarah berdirinya STIKES Harapan Ibu Jambi', keywords: 'sejarah history berdiri kampus' },
  { label: 'Visi & Misi', path: '/visi-misi', desc: 'Visi, misi dan tujuan STIKES Harapan Ibu Jambi', keywords: 'visi misi tujuan' },
  { label: 'Struktur Organisasi', path: '/struktur-organisasi', desc: 'Struktur organisasi dan jabatan pimpinan', keywords: 'struktur organisasi jabatan pimpinan ketua wakil' },
  { label: 'Logo Kampus', path: '/logo', desc: 'Logo dan lambang STIKES Harapan Ibu Jambi', keywords: 'logo lambang identitas' },
  { label: 'Mars / Hymne STIKES HI', path: '/mars-stikes', desc: 'Mars dan hymne STIKES Harapan Ibu Jambi', keywords: 'mars hymne lagu kebangsaan' },
  { label: 'Dosen & Tenaga Kependidikan', path: '/dosen-tendik', desc: 'Data dosen dan tenaga kependidikan', keywords: 'dosen tendik pengajar staff nidn nuptk' },
  { label: 'Humas', path: '/unit/humas', desc: 'Unit Hubungan Masyarakat dan layanan informasi publik STIKES HI', keywords: 'humas hubungan masyarakat kerjasama ppid informasi publik transparansi unit lembaga' },
  { label: 'Program Studi', path: '/program-studi', desc: 'Program studi S1 Kesmas, Keperawatan, Farmasi, Profesi Ners', keywords: 'prodi program studi kesmas keperawatan farmasi ners s1' },
  { label: 'Kalender Akademik', path: '/kalender', desc: 'Jadwal dan kalender akademik', keywords: 'kalender akademik jadwal semester' },
  { label: 'Beasiswa', path: '/beasiswa', desc: 'Informasi beasiswa untuk mahasiswa', keywords: 'beasiswa bantuan biaya kuliah mahasiswa' },
  { label: 'Tracer Study', path: '/tracer-study', desc: 'Tracer study dan data alumni', keywords: 'tracer study alumni lulusan kerja' },
  { label: 'Sarana & Prasarana', path: '/sarana-prasarana', desc: 'Fasilitas gedung dan laboratorium', keywords: 'sarana prasarana fasilitas gedung lab laboratorium' },
  { label: 'Berita & Pengumuman', path: '/berita', desc: 'Berita terkini dan pengumuman kampus', keywords: 'berita pengumuman news info terkini wisuda' },
  { label: 'Galeri Foto & Video', path: '/galeri', desc: 'Galeri foto dan video kegiatan kampus', keywords: 'galeri foto video dokumentasi kegiatan wisuda' },
  { label: 'Agenda / Event', path: '/agenda', desc: 'Agenda dan event kegiatan kampus', keywords: 'agenda event kegiatan acara seminar wisuda yudisium' },
  { label: 'Download File', path: '/download', desc: 'Download formulir dan dokumen penting', keywords: 'download file dokumen formulir unduh' },
  { label: 'UKM', path: '/ukm', desc: 'Unit Kegiatan Mahasiswa STIKES HI', keywords: 'ukm unit kegiatan mahasiswa ekstrakurikuler organisasi' },
  { label: 'Kontak', path: '/kontak', desc: 'Alamat, telepon dan email kampus', keywords: 'kontak alamat telepon email hubungi lokasi' },
];

const PRODI_MAP = {
  kesmas: 'Kesehatan Masyarakat',
  keperawatan: 'Keperawatan',
  farmasi: 'Farmasi',
  ners: 'Profesi Ners',
};

const getInitials = (name) =>
  name?.replace(/^(Dr\.|apt\.|Ns\.|Drs\.)?\s*/, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';

const GRAD_COLORS = [
  'from-[#7B2FBE] to-[#9333EA]',
  'from-blue-600 to-blue-800',
  'from-emerald-600 to-teal-700',
  'from-orange-500 to-amber-700',
  'from-rose-500 to-red-700',
];

const SectionHeader = ({ icon: Icon, title, count }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={18} className="text-purple-500" />
    <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300">{title}</h2>
    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full">{count} hasil</span>
  </div>
);

const PageResultCard = ({ item }) => (
  <Link to={item.path} className="block p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all bg-white dark:bg-gray-900 group">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
        <FileText size={15} className="text-purple-600 dark:text-purple-400" />
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
          {item.label}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.desc}</div>
        <div className="text-xs text-purple-400 mt-1">stikes-hi.ac.id{item.path}</div>
      </div>
    </div>
  </Link>
);

const StaffResultCard = ({ staff }) => {
  const navigate = useNavigate();
  const path = staff._type === 'dosen' ? `/dosen/${staff.id}` : `/tendik/${staff.id}`;
  const isDosen = staff._type === 'dosen';

  return (
    <div
      onClick={() => navigate(path)}
      className="block p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all bg-white dark:bg-gray-900 group cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${GRAD_COLORS[staff.id % GRAD_COLORS.length]} flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden`}>
          {staff.foto
            ? <img src={getImageUrl(staff.foto)} alt={staff.nama} className="w-10 h-10 rounded-full object-cover" />
            : getInitials(staff.nama)
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors truncate">
            {staff.nama}
          </div>
          <div className={`text-xs truncate ${isDosen ? 'text-purple-500' : 'text-teal-500'}`}>
            {isDosen ? (staff.jabatan_fungsional || '-') : (staff.jabatan || '-')}
          </div>
          <div className="text-xs text-gray-400 truncate">
            {isDosen ? (PRODI_MAP[staff.prodi] || staff.prodi || '-') : (staff.unit || '-')}
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
          isDosen
            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
            : 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
        }`}>
          {isDosen ? 'Dosen' : 'Tendik'}
        </span>
      </div>
    </div>
  );
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [inputVal, setInputVal] = useState(q);
  const [dosenList, setDosenList] = useState([]);
  const [tendikList, setTendikList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [d, t] = await Promise.all([fetchDosen(), fetchTendik()]);
      setDosenList(d);
      setTendikList(t);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => { setInputVal(q); }, [q]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputVal.trim()) {
      setSearchParams({ q: inputVal.trim() });
    }
  };

  const results = useMemo(() => {
    if (!q || q.trim().length < 2) return { pages: [], dosen: [], tendik: [] };
    const kw = q.toLowerCase();

    const pages = STATIC_PAGES.filter(p =>
      p.label.toLowerCase().includes(kw) ||
      p.desc.toLowerCase().includes(kw) ||
      p.keywords.toLowerCase().includes(kw)
    );

    const dosen = dosenList.filter(d =>
      d.nama?.toLowerCase().includes(kw) ||
      d.bidang_keahlian?.toLowerCase().includes(kw) ||
      d.jabatan_fungsional?.toLowerCase().includes(kw) ||
      (PRODI_MAP[d.prodi] || d.prodi || '').toLowerCase().includes(kw) ||
      d.nidn?.includes(kw) ||
      d.nuptk?.includes(kw)
    ).map(d => ({ ...d, _type: 'dosen' }));

    const tendik = tendikList.filter(t =>
      t.nama?.toLowerCase().includes(kw) ||
      t.jabatan?.toLowerCase().includes(kw) ||
      t.unit?.toLowerCase().includes(kw)
    ).map(t => ({ ...t, _type: 'tendik' }));

    return { pages, dosen, tendik };
  }, [q, dosenList, tendikList]);

  const totalResults = results.pages.length + results.dosen.length + results.tendik.length;

  return (
    <div className="min-h-screen bg-[#F8F7FF] dark:bg-[#121020] pt-[70px]">
      {/* Search Bar */}
      <div className="bg-white dark:bg-[#1A182E] border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={inputVal}
                onChange={e => setInputVal(e.target.value)}
                placeholder="Cari dosen, tendik, informasi kampus..."
                className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                autoFocus
              />
              {inputVal && (
                <button type="button" onClick={() => setInputVal('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>
            <button type="submit" className="px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-all">
              Cari
            </button>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-3xl mx-auto px-4 py-8">

        {loading && (
          <div className="flex justify-center py-20">
            <Loader className="animate-spin text-purple-500" size={32} />
          </div>
        )}

        {!loading && !q && (
          <div className="text-center py-20">
            <Search size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-400 dark:text-gray-500">Ketik kata kunci untuk mulai mencari</p>
          </div>
        )}

        {!loading && q && totalResults === 0 && (
          <div className="text-center py-20">
            <Search size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-600 dark:text-gray-400 font-medium mb-1">
              Tidak ada hasil untuk "<span className="text-purple-600">{q}</span>"
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">Coba kata kunci yang berbeda</p>
          </div>
        )}

        {!loading && q && totalResults > 0 && (
          <div className="space-y-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Ditemukan <span className="font-semibold text-gray-700 dark:text-gray-200">{totalResults} hasil</span> untuk "<span className="text-purple-600 dark:text-purple-400">{q}</span>"
            </p>

            {results.dosen.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <SectionHeader icon={GraduationCap} title="Dosen" count={results.dosen.length} />
                <div className="grid gap-3">
                  {results.dosen.slice(0, 6).map(s => <StaffResultCard key={s.id} staff={s} />)}
                </div>
                {results.dosen.length > 6 && (
                  <Link to="/dosen-tendik" className="block text-center text-sm text-purple-500 hover:text-purple-700 mt-3">
                    Lihat semua {results.dosen.length} dosen →
                  </Link>
                )}
              </motion.div>
            )}

            {results.tendik.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <SectionHeader icon={Users} title="Tenaga Kependidikan" count={results.tendik.length} />
                <div className="grid gap-3">
                  {results.tendik.slice(0, 4).map(s => <StaffResultCard key={s.id} staff={s} />)}
                </div>
                {results.tendik.length > 4 && (
                  <Link to="/dosen-tendik" className="block text-center text-sm text-purple-500 hover:text-purple-700 mt-3">
                    Lihat semua {results.tendik.length} tendik →
                  </Link>
                )}
              </motion.div>
            )}

            {results.pages.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <SectionHeader icon={FileText} title="Halaman" count={results.pages.length} />
                <div className="grid gap-3">
                  {results.pages.map(p => <PageResultCard key={p.path} item={p} />)}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
